import React from 'react';

import { AnimatePresence } from 'framer-motion';
import { Outlet } from 'react-router-dom';

import { EmojiIDNotFoundOverlay } from '@pkg/components';
import { Navigation } from '@pkg/components/Navigation';
import YatHandLoader from '@pkg/components/loaders/YatHandLoader';
import { useAppSelector } from '@pkg/hooks';
import { YatPageVersion } from '@pkg/types';

import YatPage from '@app/pages/YatPage';
import { useProfileNotificationsStore } from '@app/store/zustand/profile-page/useProfileNotificationsStore';

import { ProfileHeader } from './components/ProfileHeader';
import { ProfileNav } from './components/ProfileNav';
import { RedirectSheet } from './components/RedirectOverlay';
import { PROFILE_PAGE_WRAPPER_ID } from './constants';
import { FreeYatModal, PrimaryYatModal, ShowcaseYatModal } from './sections/YatsSection/sections';

import { ContentWrapper, LoaderHolder, LoaderWrapper, PageWrapper } from './ProfilePage.style';

interface Props {
    isNotFound: boolean;
    pageVersion?: YatPageVersion | null;
    eid?: string;
}

export function ProfilePage({ isNotFound, pageVersion, eid }: Props) {
    const yatRedirectModalIsOpen = useAppSelector((state) => state.dashboard.yatRedirectModalIsOpen);

    const openFreeYatModal = useProfileNotificationsStore((state) => state.openFreeYatModal);
    const openPrimaryYatModal = useProfileNotificationsStore((state) => state.openPrimaryYatModal);
    const openShowcaseYatModal = useProfileNotificationsStore((state) => state.openShowcaseYatModal);

    const loadingMarkup = (
        <LoaderWrapper>
            <LoaderHolder>
                <YatHandLoader />
            </LoaderHolder>
        </LoaderWrapper>
    );

    if (isNotFound) {
        return <EmojiIDNotFoundOverlay emojiId={eid} />;
    }

    if (pageVersion === YatPageVersion.V1) {
        return <YatPage />;
    }

    if (pageVersion === YatPageVersion.LOADING) {
        return loadingMarkup;
    }

    return (
        <>
            <Navigation hideBackground>
                <PageWrapper id={PROFILE_PAGE_WRAPPER_ID}>
                    <ProfileHeader />
                    <ProfileNav />

                    <ContentWrapper>
                        <Outlet />
                    </ContentWrapper>
                </PageWrapper>
            </Navigation>

            <AnimatePresence>
                {yatRedirectModalIsOpen ? <RedirectSheet key="ProfilePage-RedirectSheet" /> : null}
                {openFreeYatModal ? <FreeYatModal key="ProfilePage-FreeYatModal" /> : null}
                {openPrimaryYatModal ? <PrimaryYatModal key="ProfilePage-PrimaryYatModal" /> : null}
                {openShowcaseYatModal ? <ShowcaseYatModal key="ProfilePage-ShowcaseYatModal" /> : null}
            </AnimatePresence>
        </>
    );
}
